import { useEffect, useState } from 'react';
import type { Source } from '../types/Source';
import { getSources } from '../api/sources';

// Module-level cache shared by every hook caller for the page's lifetime.
// Fetched LAZILY (first hook use, e.g. InventoryPage mount); cleared on logout
// via clearSourcesCache() — next mount re-fetches. Mirrors useCrcies.
let cache: Source[] | null = null;
let inflight: Promise<Source[]> | null = null;

function loadSources(): Promise<Source[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getSources()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearSourcesCache(): void {
  cache = null;
  inflight = null;
}

interface UseSourcesResult {
  data: Source[] | null;
  loading: boolean;
  error: string | null;
}

export function useSources(): UseSourcesResult {
  const [state, setState] = useState<UseSourcesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadSources()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
